import type { RadioButtonProps } from '@hover/blueprint';
import {
  Body,
  Center,
  Flex,
  Grid,
  Heading,
  RadioButton,
  RadioButtonGroup,
  calc,
  system,
  useTheme,
} from '@hover/blueprint';
import React from 'react';
import { Trans } from 'react-i18next';

import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';
import { useShowSignInSheet } from '../../hooks/useShowSignInSheet';
import { useUploadFailure } from '../../hooks/useUploadFailure';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import { desktopNavbarHeight, mobileNavbarHeight } from '../Navbar';

import { HomeHeader } from './HomeHeader';
import { useHomeStyles } from './HomeStyles';
import { StyleImage } from './StyleImage';

const templateAreas = {
  base: `
    'image'
    'headline'
  `,
  tablet: `'headline image'`,
};

const templateColumns = {
  base: '1fr',
  tablet: '1fr 1fr',
  desktop: '1fr 1.5fr',
};

const templateRows = {
  base: '1fr auto',
  tablet: 'auto',
};

const AspectRadioButton = (props: RadioButtonProps) => (
  <RadioButton height={200} px={200} shadow="none !important" {...props} />
);

const LoggedOutHome = () => {
  const { setCurrentMenu, trackingData } = useHomeownerAppContext();
  const { aspect, setAspect } = useHomeStyles();

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  useUploadFailure();
  useShowSignInSheet();

  const { breakpoints, space } = useTheme();

  return (
    <Center
      alignItems={{ base: 'start', tablet: 'center' }}
      left={0}
      minHeight={{
        base: 'auto',
        tablet: `calc(100vh - ${desktopNavbarHeight}px)`,
      }}
      position="relative"
      top={0}
      width={{ base: '100vw', tablet: 'auto' }}
    >
      <HomeHeader
        display={{ base: 'flex', tablet: 'none' }}
        left={0}
        position="absolute"
        right={0}
        top={0}
        zIndex={1}
      />
      <Grid
        bottom={0}
        boxSizing="border-box"
        data-testid="logged-out-home-grid"
        gap={{ base: 600, tablet: 300 }}
        justifyContent="center"
        maxWidth={{
          base: `calc(${breakpoints.width500} + ${space[800]} * 2)`,
          tablet: breakpoints.width500,
        }}
        minHeight={{ base: 'var(--chakra-vh)', tablet: 800 }}
        px={{ base: 0, tablet: 800 }}
        templateAreas={templateAreas}
        templateColumns={templateColumns}
        templateRows={templateRows}
        top={0}
        w="100vw"
      >
        <StyleImage gridArea="image" />
        <Flex
          alignItems="start"
          color="neutral.0"
          direction="column"
          gap={{ base: 200, desktop: 300 }}
          gridArea="headline"
          pb={{
            base: calc.add(`${mobileNavbarHeight}px`, space[400]),
            tablet: 700,
          }}
          px={{ base: 500, tablet: 'unset' }}
        >
          <RadioButtonGroup
            colorScheme="light"
            display="flex"
            flexDirection="row"
            gap={300}
            mb={{ tablet: 200 }}
            onChange={setAspect}
            size="small"
            value={aspect}
            variant="ghost"
          >
            <AspectRadioButton value="exterior">
              {t('home.aspects.exterior')}
            </AspectRadioButton>
            <AspectRadioButton value="interior">
              {t('home.aspects.interior')}
            </AspectRadioButton>
          </RadioButtonGroup>
          <Heading
            as="h2"
            color="inherit"
            mb="0 !important"
            size={{ base: 700, tablet: 900 }}
          >
            <Trans
              components={{
                LineBreak: <system.br display="initial" />,
              }}
              i18nKey="home.homeMadeInAnInstant"
              t={t}
            />
          </Heading>
          <Body
            as="h1"
            color="inherit"
            m={0}
            opacity={0.8}
            width={{ base: '90%', tablet: '75%' }}
          >
            {t('home.transformAPhoto')}
          </Body>
          <Button
            colorScheme="light"
            mt={{ base: 200, tablet: 500 }}
            onClick={() => setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO)}
            optionalTrackingProperties={{
              ...trackingData,
              button_location: 'mid-left-main-page',
              track_id: 'add-photo-landing-page-button',
            }}
            size="large"
            width={{ base: '100%', tablet: 'auto' }}
          >
            {t('home.tryItWithYourPlace')}
          </Button>
        </Flex>
      </Grid>
    </Center>
  );
};

export { LoggedOutHome };
